import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';
import './AcademicYearConfig.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const AcademicYearConfig = () => {
  const { showSuccess, showError } = useNotificationContext();
  
  // State management
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingDays, setEditingDays] = useState(false);
  const [editingTimeslots, setEditingTimeslots] = useState(false);
  const [editingBreaks, setEditingBreaks] = useState(false);
  
  // Form data
  const [daysData, setDaysData] = useState([]);
  const [timeslotsData, setTimeslotsData] = useState([]);
  const [lunchBreakEnabled, setLunchBreakEnabled] = useState(false);
  const [lunchBreakStart, setLunchBreakStart] = useState('13:30');
  const [lunchBreakEnd, setLunchBreakEnd] = useState('14:30');

  // Fetch academic years
  useEffect(() => {
    fetchAcademicYears();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/academic-years?limit=1000`);
      const years = Array.isArray(response.data) ? response.data : response.data.data || [];
      setAcademicYears(years);
      if (years.length > 0 && !selectedYear) {
        setSelectedYear(years[0]._id);
      }
    } catch (error) {
      showError('Failed to load academic years');
    }
  };

  // Fetch config when year is selected
  useEffect(() => {
    if (selectedYear) {
      fetchConfig();
    }
  }, [selectedYear]);

  const fetchConfig = async () => {
    if (!selectedYear) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/academic-year-config/${selectedYear}`
      );
      
      const configData = response.data.data || response.data;
      setConfig(configData);
      
      // Populate form data
      setDaysData(configData.daysOfWeek || []);
      setTimeslotsData(configData.timeslots || []);
      setLunchBreakEnabled(!!configData.lunchBreakStart || !!configData.lunchBreakEnd);
      setLunchBreakStart(configData.lunchBreakStart || '13:30');
      setLunchBreakEnd(configData.lunchBreakEnd || '14:30');
    } catch (error) {
      if (error.response?.status === 404) {
        // No config exists yet
        loadDefaultTemplate();
      } else {
        showError('Failed to load configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/academic-year-config/template`);
      const template = response.data.data;
      
      setDaysData(template.daysOfWeek);
      setTimeslotsData(template.timeslots);
      setLunchBreakEnabled(false);
      setLunchBreakStart(template.lunchBreakStart);
      setLunchBreakEnd(template.lunchBreakEnd);
      setConfig(null);
    } catch (error) {
      showError('Failed to load default template');
    }
  };

  // Save configuration
  const saveConfig = async () => {
    if (!selectedYear) {
      showError('Please select an academic year');
      return;
    }

    if (daysData.length === 0) {
      showError('Please configure at least one working day');
      return;
    }

    if (timeslotsData.length === 0) {
      showError('Please configure at least one timeslot');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        academicYear: selectedYear,
        daysOfWeek: daysData,
        timeslots: timeslotsData,
        lunchBreakStart: lunchBreakEnabled ? lunchBreakStart : null,
        lunchBreakEnd: lunchBreakEnabled ? lunchBreakEnd : null
      };

      const response = await axios.post(
        `${API_URL}/api/academic-year-config`,
        payload
      );

      const savedConfig = response.data.data;
      setConfig(savedConfig);
      
      // Update form data with saved values to ensure consistency
      setDaysData(savedConfig.daysOfWeek || []);
      setTimeslotsData(savedConfig.timeslots || []);
      setLunchBreakEnabled(!!savedConfig.lunchBreakStart || !!savedConfig.lunchBreakEnd);
      setLunchBreakStart(savedConfig.lunchBreakStart || '13:30');
      setLunchBreakEnd(savedConfig.lunchBreakEnd || '14:30');
      
      showSuccess('Configuration saved successfully');
      setEditingDays(false);
      setEditingTimeslots(false);
      setEditingBreaks(false);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  // Handle day changes
  const handleDayChange = (index, field, value) => {
    const updatedDays = [...daysData];
    updatedDays[index] = {
      ...updatedDays[index],
      [field]: field === 'isWorking' ? !updatedDays[index][field] : value
    };
    setDaysData(updatedDays);
  };

  // Handle timeslot changes
  const handleTimeslotChange = (index, field, value) => {
    const updatedSlots = [...timeslotsData];
    updatedSlots[index] = {
      ...updatedSlots[index],
      [field]: field === 'duration' || field === 'breakDuration' ? parseInt(value) : value
    };
    setTimeslotsData(updatedSlots);
  };

  // Add new timeslot
  const addTimeslot = () => {
    const newSlot = {
      slotNumber: timeslotsData.length + 1,
      startTime: '08:30',
      endTime: '10:00',
      duration: 90,
      breakDuration: 15,
      order: timeslotsData.length + 1
    };
    setTimeslotsData([...timeslotsData, newSlot]);
  };

  // Handle lunch break toggle - auto-save
  const handleLunchBreakToggle = (newState) => {
    setLunchBreakEnabled(newState);
    // Auto-save immediately after toggle
    setTimeout(() => {
      saveLunchBreakChange(newState);
    }, 0);
  };

  // Save lunch break change to database
  const saveLunchBreakChange = async (isEnabled) => {
    if (!selectedYear) {
      showError('Please select an academic year');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        academicYear: selectedYear,
        daysOfWeek: daysData,
        timeslots: timeslotsData,
        lunchBreakStart: isEnabled ? lunchBreakStart : null,
        lunchBreakEnd: isEnabled ? lunchBreakEnd : null
      };

      const response = await axios.post(
        `${API_URL}/api/academic-year-config`,
        payload
      );

      const savedConfig = response.data.data;
      setConfig(savedConfig);
      
      // Sync all form states with saved data
      setDaysData(savedConfig.daysOfWeek || []);
      setTimeslotsData(savedConfig.timeslots || []);
      setLunchBreakEnabled(!!savedConfig.lunchBreakStart || !!savedConfig.lunchBreakEnd);
      setLunchBreakStart(savedConfig.lunchBreakStart || '13:30');
      setLunchBreakEnd(savedConfig.lunchBreakEnd || '14:30');
      
      showSuccess(`Pause déjeuner ${isEnabled ? 'activée' : 'désactivée'}`);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save lunch break configuration');
      // Revert the toggle if save fails
      setLunchBreakEnabled(!isEnabled);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="academic-year-config-container">
      <div className="config-header">
        <h1>⚙️ Configuration Académique</h1>
        <p>Configurez les jours de la semaine et les créneaux horaires pour chaque année académique</p>
      </div>

      <div className="config-content">
        {/* Academic Year Selector */}
        <div className="year-selector-section">
          <label className="form-label">Année Académique</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="form-select"
            disabled={loading}
          >
            <option value="">Sélectionner une année</option>
            {academicYears.map(year => (
              <option key={year._id} value={year._id}>
                {year.name} {year.isActive ? '(Active)' : ''}
              </option>
            ))}
          </select>
        </div>

        {selectedYear && (
          <>
            {/* Days of Week Configuration */}
            <div className="config-section">
              <div className="section-header">
                <h2>📅 Jours de la Semaine</h2>
                <button
                  onClick={() => setEditingDays(!editingDays)}
                  className="btn-toggle-edit"
                >
                  {editingDays ? '❌ Annuler' : '✏️ Modifier'}
                </button>
              </div>

              {editingDays ? (
                <div className="days-grid">
                  {daysData.map((day, index) => (
                    <div key={index} className="day-item">
                      <div className="day-card">
                        <div className="day-name">{day.dayName}</div>
                        <label className="checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={day.isWorking}
                            onChange={() => handleDayChange(index, 'isWorking', null)}
                          />
                          <span>Jour ouvrable</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="6"
                          value={day.dayNumber}
                          onChange={(e) => handleDayChange(index, 'dayNumber', e.target.value)}
                          placeholder="Numéro du jour"
                          className="form-input-small"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="days-display">
                  {daysData.map((day, index) => (
                    <div
                      key={index}
                      className={`day-badge ${day.isWorking ? 'working' : 'non-working'}`}
                    >
                      {day.dayName}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Timeslots Configuration */}
            <div className="config-section">
              <div className="section-header">
                <h2>🕐 Créneaux Horaires</h2>
                <div className="button-group">
                  <button
                    onClick={() => setEditingTimeslots(!editingTimeslots)}
                    className="btn-toggle-edit"
                  >
                    {editingTimeslots ? '❌ Annuler' : '✏️ Modifier'}
                  </button>
                  {editingTimeslots && (
                    <button onClick={addTimeslot} className="btn-add">
                      ➕ Ajouter Créneau
                    </button>
                  )}
                </div>
              </div>

              {editingTimeslots ? (
                <div className="timeslots-grid">
                  {timeslotsData.map((slot, index) => (
                    <div key={index} className="timeslot-item">
                      <div className="timeslot-card">
                        <div className="form-group">
                          <label>Numéro du Créneau</label>
                          <input
                            type="number"
                            min="1"
                            value={slot.slotNumber}
                            onChange={(e) => handleTimeslotChange(index, 'slotNumber', e.target.value)}
                            className="form-input-small"
                          />
                        </div>
                        <div className="form-group">
                          <label>Heure de Début</label>
                          <input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) => handleTimeslotChange(index, 'startTime', e.target.value)}
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>Heure de Fin</label>
                          <input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) => handleTimeslotChange(index, 'endTime', e.target.value)}
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>Durée (minutes)</label>
                          <input
                            type="number"
                            min="30"
                            max="240"
                            value={slot.duration}
                            onChange={(e) => handleTimeslotChange(index, 'duration', e.target.value)}
                            className="form-input-small"
                          />
                        </div>
                        <div className="form-group">
                          <label>Pause après (minutes)</label>
                          <input
                            type="number"
                            min="0"
                            max="60"
                            value={slot.breakDuration}
                            onChange={(e) => handleTimeslotChange(index, 'breakDuration', e.target.value)}
                            className="form-input-small"
                          />
                        </div>
                        {timeslotsData.length > 1 && (
                          <button
                            onClick={() => removeTimeslot(index)}
                            className="btn-remove"
                          >
                            ❌ Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="timeslots-display">
                  {timeslotsData.map((slot, index) => (
                    <div key={index} className="timeslot-badge">
                      <div className="slot-time">{slot.startTime} - {slot.endTime}</div>
                      <div className="slot-duration">({slot.duration} min)</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Breaks Configuration */}
            <div className="config-section">
              <div className="section-header">
                <h2>🍽️ Pause Déjeuner</h2>
                <div className="button-group">
                  <label className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={lunchBreakEnabled}
                      onChange={(e) => handleLunchBreakToggle(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Activer la pause déjeuner</span>
                  </label>
                  {lunchBreakEnabled && (
                    <button
                      onClick={() => setEditingBreaks(!editingBreaks)}
                      className="btn-toggle-edit"
                    >
                      {editingBreaks ? '❌ Annuler' : '✏️ Modifier'}
                    </button>
                  )}
                </div>
              </div>

              {lunchBreakEnabled && (
                <>
                  {editingBreaks ? (
                    <div className="breaks-grid">
                      <div className="break-item">
                        <h3>Pause Déjeuner</h3>
                        <div className="form-group">
                          <label>Début</label>
                          <input
                            type="time"
                            value={lunchBreakStart}
                            onChange={(e) => setLunchBreakStart(e.target.value)}
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>Fin</label>
                          <input
                            type="time"
                            value={lunchBreakEnd}
                            onChange={(e) => setLunchBreakEnd(e.target.value)}
                            className="form-input"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="breaks-display">
                      <div className="break-badge">
                        <strong>🍽️ Déjeuner:</strong> {lunchBreakStart} - {lunchBreakEnd}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Save Button */}
            {(editingDays || editingTimeslots || editingBreaks) && (
              <div className="config-actions">
                <button
                  onClick={saveConfig}
                  disabled={loading}
                  className="btn-save"
                >
                  {loading ? '⏳ Sauvegarde...' : '💾 Enregistrer Configuration'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AcademicYearConfig;
